import { isStandardRole, useUserRoleForCurrentWorkspace } from '@/features/roles/hook';
import { authStore } from '@/services/auth/store';
import { useMasqLink } from '@/utils/navigation';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Role } from '@customerio/hydra-permissions/roles';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useWorkspaceSettingsLink } from '@/hooks/use-workspace-settings-link';
import { useSampleWorkspace } from '@/utils/sample-workspace';
import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';

function currentUserRoleString(role: string) {
  const roleStrings: Record<Role, string> = {
    [Role.Admin]: 'an Account admin',
    [Role.Manager]: 'a Workspace admin',
    [Role.Author]: 'an Author',
    [Role.AuthorRedacted]: 'an Author',
    [Role.AuthorRedactedSensitive]: 'an Author',
    [Role.AuthorRedactedAll]: 'an Author',
    [Role.Viewer]: 'a Viewer',
    [Role.ViewerRedacted]: 'a Viewer',
    [Role.ViewerRedactedSensitive]: 'a Viewer',
    [Role.ViewerRedactedAll]: 'a Viewer',
  };

  if (isStandardRole(role)) {
    return `You're ${roleStrings[role]}`;
  }

  return role;
}

export function PersonDropdown() {
  const auth = useStore(authStore);
  const { hasPermission } = usePermissions();
  const masqLink = useMasqLink();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const workspaceSettingsLink = useWorkspaceSettingsLink();
  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();

  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  const isSetupGuideIncomplete = !isSetupGuideLoading && setupGuide.completed_at == null;

  const showSetUpWorkspaceNavItem =
    isSetupGuideIncomplete &&
    window.sessionStorage.getItem('showSetUpWorkspaceNavItem') === 'true' &&
    !isSampleWorkspace(currentWorkspace?.id);

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(false);

  useEffect(() => {
    try {
      const cioSettings = JSON.parse(localStorage.getItem('storage:cio-settings') ?? '{}');
      setIsShowingMasqBar(cioSettings?.showAdmin ?? false);
    } catch {
      // ignore
    }
  }, []);

  const onMasqueradeChange = (event: React.FormEvent<HTMLInputElement>) => {
    event.preventDefault();

    try {
      const cioSettings = JSON.parse(localStorage.getItem('storage:cio-settings') ?? '{}');
      cioSettings.showAdmin = !((cioSettings.showAdmin as boolean | null) ?? false);

      localStorage.setItem('storage:cio-settings', JSON.stringify(cioSettings));
      setIsShowingMasqBar(cioSettings.showAdmin ?? false);
      // Reload the page, so that the Journeys UI picks up the local storage
      // update
      window.location.reload();
    } catch {
      // ignore
    }
  };

  return (
    <>
      <DropdownMenu>
        {(context) => (
          <IconButton
            label="Menu"
            icon="manage"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          />
        )}
        <DropdownMenuContent>
          {userRole != null || showSetUpWorkspaceNavItem ? (
            <DropdownMenuGroup>
              {userRole != null ? (
                <DropdownMenuItem>
                  {currentUserRoleString(userRole.name)}
                  {userRole.redacted ? (
                    <div style={{ width: '20px', height: '20px', marginLeft: '8px' }}>
                      <Icon name="view-off" size="fill" display="block" />
                    </div>
                  ) : null}
                </DropdownMenuItem>
              ) : null}
              {showSetUpWorkspaceNavItem ? (
                <DropdownMenuItem href={masqLink(`/workspaces/${currentWorkspace?.id}/journeys/home/workspace-setup`)}>
                  Set up workspace
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
          ) : null}

          <DropdownMenuGroup>
            <DropdownMenuItem href={masqLink('/settings/personal')}>Personal settings</DropdownMenuItem>
            <DropdownMenuItem href={masqLink('/settings/experimental-features')}>
              Experimental features
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem href={masqLink('/settings')}>Account settings</DropdownMenuItem>
            <DropdownMenuItem href={masqLink('/settings/privacy')}>Privacy, Data &amp; AI</DropdownMenuItem>
            {hasPermission('account/billing/view') ? (
              <DropdownMenuItem href={masqLink('/settings/billing/your-plan')}>Plans &amp; Billing</DropdownMenuItem>
            ) : null}
            <DropdownMenuItem href={workspaceSettingsLink(currentWorkspace?.id ?? 'last')}>
              Workspace settings
            </DropdownMenuItem>
          </DropdownMenuGroup>
          {showAdminMasqueradeOption ? (
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Box display="flex" alignItems="center">
                  <Text
                    mr="400"
                    display="inline"
                    as="label"
                    id="toggle-admin-masq-label"
                    htmlFor="toggle-admin-masq"
                    textTransform="capitalize"
                  >
                    Admin masquerade
                  </Text>
                  <Toggle
                    size="sm"
                    labelOn="On"
                    labelOff="Off"
                    onChange={(event) => onMasqueradeChange(event)}
                    ariaLabelledby="toggle-admin-masq-label"
                    id="toggle-admin-masq"
                    isChecked={isShowingMasqBar}
                  />
                </Box>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          ) : null}
          <DropdownMenuGroup>
            <DropdownMenuItem href="/logout" isDanger={true}>
              Log out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
